import React from 'react';
import styled from 'styled-components';
import Box from '@material-ui/core/Box';
import {ValueFormatter} from '@epic-visualizations/charts';

import Trend from '../../../../../trend/Trend';
import {getDirectionStyles} from '../../../../../../utils/ThemeUtils';
import useLocalization from '../../../../../../hooks/useLocalization';

import {KeysMap, Trends} from './types';
import {Payout} from '../../../../../../types';

const CODES = {
    metric: 'epic.affiliate.fortnite_games.rewards.metric.',
    total: 'epic.affiliate.fortnite_games.rewards.metric.total'
};

export const StyledTooltipContainer = styled.div`
    margin-top: ${p => p.theme.muiTheme.spacing(0.5)}px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: max-content;
`;

export const StyledTotalContainer = styled.div`
    display: flex;
    & > span {
        font-weight: bold;
    }
`;

export const StyledLabel = styled.span`
    min-width: 110px;
    margin: ${p =>
        getDirectionStyles(p.theme.muiTheme.direction)(
            p.theme.muiTheme.spacing(0, 3, 0, 0),
            p.theme.muiTheme.spacing(0, 0, 0, 3)
        )};
`;

export const StyledValue = styled.span`
    min-width: 80px;
`;

export const StyledTotalLabel = styled.span`
    min-width: 128px;
    margin: ${p =>
        getDirectionStyles(p.theme.muiTheme.direction)(
            p.theme.muiTheme.spacing(0, 3, 0, 0),
            p.theme.muiTheme.spacing(0, 0, 0, 3)
        )};
`;

export const StyledLegendMarker = styled.div`
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;

    margin: ${p =>
        getDirectionStyles(p.theme.muiTheme.direction)(
            p.theme.muiTheme.spacing(0, 2, 0, 0),
            p.theme.muiTheme.spacing(0, 0, 0, 2)
        )};
`;

export interface CustomTooltipParams {
    trends?: Trends;
    tooltipFormatter: ValueFormatter<string | number>;
    keysMap?: KeysMap;
}

const RewardsTooltip = ({trends, tooltipFormatter}: CustomTooltipParams) => {
    const getMessage = useLocalization();

    if (!trends) {
        return undefined;
    }

    return ({payload}: {payload: any}) => (
        <Box display="flex" flexDirection="column">
            {(payload || []).map(({name, dataKey, value, color, payload}) => {
                const trendsPayload = trends[payload.time.toString()];
                const trend = trendsPayload[name];
                const {total: totalTrend} = trendsPayload;
                const {engagement, sac} = payload;

                const payouts = Object.values(payload) || [];
                const isCombinedTrend = payouts.filter(value => value).length > 2;

                return (
                    <React.Fragment key={dataKey}>
                        {!!value && (
                            <StyledTooltipContainer>
                                <StyledLegendMarker
                                    style={{
                                        backgroundColor: `${color || (payload as any).fill}`
                                    }}
                                />
                                <StyledLabel>{getMessage(`${CODES.metric}${dataKey}`)}</StyledLabel>
                                <StyledValue>
                                    {tooltipFormatter ? tooltipFormatter(value) : Math.ceil(value)}
                                </StyledValue>
                                <Box mx="8px">
                                    <Trend value={trend} />
                                </Box>
                            </StyledTooltipContainer>
                        )}
                        {isCombinedTrend && dataKey === Payout.SAC && (
                            <StyledTooltipContainer>
                                <StyledTotalContainer>
                                    <StyledTotalLabel>{getMessage(CODES.total)}</StyledTotalLabel>
                                    <StyledValue>
                                        {tooltipFormatter
                                            ? tooltipFormatter(engagement + sac)
                                            : Math.ceil(engagement + sac)}
                                    </StyledValue>
                                    <Box mx="8px">
                                        <Trend value={totalTrend} />
                                    </Box>
                                </StyledTotalContainer>
                            </StyledTooltipContainer>
                        )}
                    </React.Fragment>
                );
            })}
        </Box>
    );
};

export default RewardsTooltip;
